import { useCallback, useEffect } from 'react';
import { PrimitiveAtom, useAtomValue, useSetAtom } from 'jotai';
import queryString from 'query-string';
import { updateUrl } from '@/utils';

export type PoiDeeplinkState = {
  shouldUpdateUrl: boolean;
  selectedPointOfInterestIdAtom: PrimitiveAtom<string | undefined>;
  showOnlySelectedPoiAtom: PrimitiveAtom<boolean>;
  showPointOfInterestPointerAtom: PrimitiveAtom<boolean>;
  onPoiParsed?: (poiId: string) => void;
  onShowOnlySelectedPoiParsed?: (showOnly: boolean) => void;
};

export const usePoiDeeplink = (state: PoiDeeplinkState) => {
  const {
    shouldUpdateUrl,
    selectedPointOfInterestIdAtom,
    showOnlySelectedPoiAtom,
    showPointOfInterestPointerAtom,
    onPoiParsed,
    onShowOnlySelectedPoiParsed,
  } = state;

  const pointOfInterestId = useAtomValue(selectedPointOfInterestIdAtom);
  const showOnlySelectedPoi = useAtomValue(showOnlySelectedPoiAtom);
  const showPointOfInterestPointer = useAtomValue(showPointOfInterestPointerAtom);

  const setPointOfInterestId = useSetAtom(selectedPointOfInterestIdAtom);
  const setShowOnlySelectedPoi = useSetAtom(showOnlySelectedPoiAtom);
  const setShowPointOfInterestPointer = useSetAtom(showPointOfInterestPointerAtom);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl('poi', pointOfInterestId);
    }
  }, [shouldUpdateUrl, pointOfInterestId]);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl('showOnlySelectedPoi', showOnlySelectedPoi ? 'true' : 'false');
    }
  }, [shouldUpdateUrl, showOnlySelectedPoi]);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl('showPointOfInterestPointer', showPointOfInterestPointer ? 'true' : 'false');
    }
  }, [shouldUpdateUrl, showPointOfInterestPointer]);

  const applyFromParsed = useCallback(
    (parsed: queryString.ParsedQuery<string>) => {
      if (parsed.poi) {
        const poiId = parsed.poi.toString();
        setPointOfInterestId(poiId);
        onPoiParsed?.(poiId);
      }

      if (parsed.poi && parsed.showOnlySelectedPoi) {
        const showOnly = parsed.showOnlySelectedPoi === 'true';
        setShowOnlySelectedPoi(showOnly);
        onShowOnlySelectedPoiParsed?.(showOnly);
      }

      if (parsed.showPointOfInterestPointer) {
        setShowPointOfInterestPointer(parsed.showPointOfInterestPointer === 'true');
      }
    },
    [
      setPointOfInterestId,
      onPoiParsed,
      setShowOnlySelectedPoi,
      onShowOnlySelectedPoiParsed,
      setShowPointOfInterestPointer,
    ]
  );

  return { applyFromParsed };
};
